import { Button } from "@izicvs/ui";
import { cn } from "@izicvs/utils";
import { t } from "@lingui/macro";
import { motion } from "framer-motion";
import { Bot, Building2, Check, Coins, Crown, Rocket, Sparkles, Tag } from "lucide-react";
import { Link, useNavigate } from "react-router";

import { GlowingEffect } from "@/client/components/glowing-effect";
import { Seo } from "@/client/components/seo";
import { useBuyTokens, useCheckout, useTemplatesPass } from "@/client/services/billing";
import { useAuthStore } from "@/client/stores/auth";

import { PageHero } from "../_components/page-shell";

type Tier = {
  name: string;
  icon: React.ElementType;
  price: string;
  period: string;
  tagline: string;
  cta: string;
  featured?: boolean;
  action: "register" | "pro" | "autopilot";
  features: string[];
};

export const PricingPage = () => {
  const isLoggedIn = useAuthStore((state) => !!state.user);
  const navigate = useNavigate();
  const { checkout, loading: checkingOut } = useCheckout();
  const { subscribe: startTemplatesPass, loading: startingPass } = useTemplatesPass();
  const { purchase, loading: buyingTokens } = useBuyTokens();

  // Logged-in visitors go straight to Stripe Checkout; guests are sent to
  // sign up, then land on the subscription settings to complete the upgrade.
  const requireAuthThen = (action: () => void) => {
    if (isLoggedIn) action();
    else void navigate("/auth/register?redirect=/dashboard/settings");
  };

  const tiers: Tier[] = [
    {
      name: t`Free`,
      icon: Sparkles,
      price: "0 €",
      period: t`forever`,
      tagline: t`Everything you need to build a resume and start your search.`,
      cta: t`Start for free`,
      action: "register",
      features: [
        t`Resume builder with every template`,
        t`Unlimited PDF exports`,
        t`Live ATS readiness check`,
        t`Job search across every source`,
        t`"For You" ranked feed`,
        t`Application tracker with outcome stats`,
        t`AI writing assistant, monthly allowance`,
      ],
    },
    {
      name: t`Pro`,
      icon: Rocket,
      price: "9 €",
      period: t`per month`,
      tagline: t`The full AI toolkit to apply smarter and stand out.`,
      cta: t`Go Pro`,
      featured: true,
      action: "pro",
      features: [
        t`Everything in Free`,
        t`Full AI suite, generous fair-use limit`,
        t`Expert AI panel job match`,
        t`Interview simulator with scored feedback`,
        t`Tailored resume and cover letter per job`,
        t`One-click apply, pre-filled by AI`,
        t`Scam and ghost-job radar`,
        t`Unlimited resumes`,
      ],
    },
    {
      name: t`Autopilot`,
      icon: Bot,
      price: "19 €",
      period: t`per month`,
      tagline: t`Let the agent run your search around the clock.`,
      cta: t`Go Autopilot`,
      action: "autopilot",
      features: [
        t`Everything in Pro`,
        t`Autonomous 24/7 job search`,
        t`Applications prepared for you automatically`,
        t`Daily digest of your best new matches`,
        t`Batch review and approve`,
        t`Priority support`,
      ],
    },
  ];

  return (
    <>
      <Seo
        title={t`Pricing, plans that grow with your job search`}
        description={t`Start free, then upgrade to Pro or Autopilot when you want the AI to do the heavy lifting. Cancel anytime.`}
        path="/pricing"
        breadcrumb={t`Pricing`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Izicvs",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          offers: [
            { "@type": "Offer", name: "Free", price: "0", priceCurrency: "EUR" },
            { "@type": "Offer", name: "Pro", price: "9", priceCurrency: "EUR" },
            { "@type": "Offer", name: "Autopilot", price: "19", priceCurrency: "EUR" },
          ],
        }}
      />

      <PageHero
        eyebrow={t`Pricing`}
        icon={Tag}
        title={t`Simple pricing that grows with your search`}
        subtitle={t`Start free. Upgrade when you want the AI to do the heavy lifting. Cancel anytime.`}
      />

      <section className="container mx-auto px-6 py-16 lg:px-8">
        <div className="grid items-stretch gap-6 lg:grid-cols-3">
          {tiers.map((tier, index) => {
            const Icon = tier.icon;
            return (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.45, delay: index * 0.1 },
                }}
                viewport={{ once: true }}
                className={cn("list-none", tier.featured && "lg:-mt-4")}
              >
                <div className="rounded-2.5xl relative h-full border p-2 md:rounded-3xl md:p-3">
                  {tier.featured && (
                    <span className="absolute -top-2.5 left-1/2 z-10 -translate-x-1/2 rounded-full bg-[linear-gradient(90deg,#dd7bbb,#d79f1e,#5a922c,#4c7894)] px-4 py-1 text-xs font-semibold text-white shadow-md">
                      {t`Most popular`}
                    </span>
                  )}
                  <GlowingEffect
                    glow
                    spread={40}
                    disabled={false}
                    proximity={64}
                    inactiveZone={0.01}
                  />
                  <div className="border-0.75 relative flex h-full flex-col overflow-hidden rounded-xl p-6 dark:shadow-[0px_0px_27px_0px_#2D2D2D] md:p-8">
                    <div className="flex items-center gap-3">
                      <div className="w-fit rounded-lg border border-gray-600 p-2">
                        <Icon className="size-4 text-black dark:text-neutral-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-black dark:text-white">
                        {tier.name}
                      </h3>
                    </div>

                    <div className="mt-6 flex items-baseline gap-1.5">
                      <span className="text-5xl font-black tracking-tight text-black dark:text-white">
                        {tier.price}
                      </span>
                      <span className="text-sm text-neutral-500 dark:text-neutral-400">
                        {tier.period}
                      </span>
                    </div>
                    <p className="mt-3 min-h-11 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">
                      {tier.tagline}
                    </p>

                    {tier.action === "register" ? (
                      <Button
                        asChild
                        size="lg"
                        variant={tier.featured ? "primary" : "outline"}
                        className="mt-7 w-full"
                      >
                        <Link to="/auth/register">{tier.cta}</Link>
                      </Button>
                    ) : (
                      <Button
                        size="lg"
                        variant={tier.featured ? "primary" : "outline"}
                        className="mt-7 w-full"
                        disabled={checkingOut}
                        onClick={() => {
                          requireAuthThen(() => void checkout(tier.action as "pro" | "autopilot"));
                        }}
                      >
                        {tier.cta}
                      </Button>
                    )}

                    <div className="mt-8 h-px bg-border" />

                    <ul className="mt-6 space-y-3.5 text-sm">
                      {tier.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-2.5">
                          <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-emerald-500/15">
                            <Check className="size-3 text-emerald-500" strokeWidth={3} />
                          </span>
                          <span className="text-neutral-700 dark:text-neutral-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Teams / Organizations */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0, transition: { duration: 0.45 } }}
          viewport={{ once: true }}
          className="mt-6 flex flex-col items-start gap-6 rounded-3xl border p-8 md:flex-row md:items-center md:justify-between md:p-10"
        >
          <div className="max-w-2xl">
            <div className="flex items-center gap-3">
              <div className="w-fit rounded-lg border border-gray-600 p-2">
                <Building2 className="size-4 text-black dark:text-neutral-400" />
              </div>
              <h3 className="text-lg font-semibold text-black dark:text-white">
                {t`Teams & Organizations`}
              </h3>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">
              {t`For coaches, bootcamps and universities. Equip your people per seat and track your whole cohort's results from one dashboard.`}
            </p>
            <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-neutral-700 dark:text-neutral-300">
              {[
                t`Per-seat billing`,
                t`Cohort progress dashboard`,
                t`Your branding and logo`,
                t`Central seat management`,
              ].map((feature) => (
                <li key={feature} className="flex items-center gap-2">
                  <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-emerald-500/15">
                    <Check className="size-3 text-emerald-500" strokeWidth={3} />
                  </span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
          <div className="shrink-0 text-left md:text-right">
            <div className="flex items-baseline gap-1.5 md:justify-end">
              <span className="text-sm text-neutral-500">{t`from`}</span>
              <span className="text-4xl font-black tracking-tight text-black dark:text-white">
                29 €
              </span>
              <span className="text-sm text-neutral-500">{t`/ seat`}</span>
            </div>
            <p className="mt-1 text-xs text-neutral-500">
              {t`Volume pricing for bootcamps and universities.`}
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row md:justify-end">
              <Button asChild size="lg">
                <Link to="/auth/register">{t`Start a team`}</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/contact">{t`Contact sales`}</Link>
              </Button>
            </div>
          </div>
        </motion.div>

        {/* À la carte add-ons, available on every plan (including Free) */}
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {/* Weekly premium-templates pass */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0, transition: { duration: 0.45 } }}
            viewport={{ once: true }}
            className="flex flex-col gap-4 rounded-3xl border border-dashed p-8"
          >
            <div className="flex items-start gap-3">
              <div className="w-fit rounded-lg border border-gray-600 p-2">
                <Crown className="size-4 text-black dark:text-neutral-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-black dark:text-white">
                  {t`Premium templates pass`}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">
                  {t`Want a premium design without the full plan? Unlock every premium template for 2,99 € / week. Cancel anytime. Pro and Autopilot already include them.`}
                </p>
              </div>
            </div>
            <Button
              size="lg"
              variant="outline"
              className="w-fit"
              disabled={startingPass}
              onClick={() => {
                requireAuthThen(() => void startTemplatesPass());
              }}
            >
              {t`Unlock templates`}
            </Button>
          </motion.div>

          {/* Pay-as-you-go AI token top-up */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0, transition: { duration: 0.45 } }}
            viewport={{ once: true }}
            className="flex flex-col gap-4 rounded-3xl border border-dashed p-8"
          >
            <div className="flex items-start gap-3">
              <div className="w-fit rounded-lg border border-gray-600 p-2">
                <Coins className="size-4 text-black dark:text-neutral-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-black dark:text-white">
                  {t`Need more AI? Top up anytime.`}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">
                  {t`Reached your monthly AI allowance? Buy token packs on any plan, including Free. Purchased tokens never expire, and organizations top up a shared pool for their whole cohort.`}
                </p>
              </div>
            </div>
            <Button
              size="lg"
              variant="outline"
              className="w-fit"
              disabled={buyingTokens}
              onClick={() => {
                requireAuthThen(() => void purchase(1));
              }}
            >
              {t`Get started`}
            </Button>
          </motion.div>
        </div>

        <p className="mx-auto mt-12 max-w-2xl text-center text-sm text-neutral-500">
          {t`Prices in euros, taxes included. You can upgrade, downgrade or cancel your plan at any time from your dashboard.`}
        </p>
      </section>
    </>
  );
};
